import {
	WorkfrontMappingKind,
	WorkfrontProcess,
	WorkfrontRunStatus,
	WorkfrontSyncErrorSeverity,
	WorkfrontSyncErrorStatus,
	WorkfrontSyncErrorStep,
} from '@atlas/types'
import { CardShell, PaginationFooter, SectionCard } from '@atlas/ui-components'
import { Spinner } from '@atlas/ui-primitives'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useWorkfrontError, useWorkfrontErrors, useWorkfrontRun, useWorkfrontRuns } from '@/hooks/useWorkfront'
import { apiErrorMessage } from '@/lib/api-error'
import { formatUtc } from '@/lib/format-utc'
import { ErrorDetailCard, ErrorsTable } from './components/ErrorsTable'
import { RunsTable, StepRunsTable, statsLine } from './components/RunsTable'
import { humanize, RunStatusBadge } from './components/StatusBadges'
import { EnumFilter, PAGE_SIZE, useErrorActions, WORKFRONT_PATHS, WorkfrontHeader } from './shared'

function Pager(props: {
	page: number
	totalPages: number
	total: number
	noun: string
	nounPlural: string
	isFetching: boolean
	onPage: (page: number) => void
}) {
	return <PaginationFooter {...props} onPage={(next) => props.onPage(Math.min(Math.max(1, next), props.totalPages))} />
}

/** The adapter's runs, filterable by process and status. */
export function WorkfrontRunsPage() {
	const [process, setProcess] = useState<WorkfrontProcess | undefined>()
	const [status, setStatus] = useState<WorkfrontRunStatus | undefined>()
	const [page, setPage] = useState(1)
	const query = useWorkfrontRuns({ process, status, page, limit: PAGE_SIZE })
	useEffect(() => setPage(1), [process, status])
	return (
		<div className="space-y-6">
			<WorkfrontHeader title="Runs" />
			<SectionCard title="Filters">
				<div className="grid gap-4 md:grid-cols-4">
					<EnumFilter label="Process" values={Object.values(WorkfrontProcess)} value={process} onChange={setProcess} />
					<EnumFilter label="Status" values={Object.values(WorkfrontRunStatus)} value={status} onChange={setStatus} />
				</div>
			</SectionCard>
			<CardShell>
				<RunsTable
					runs={query.data?.data ?? []}
					isLoading={query.isLoading}
					isError={query.isError}
					hrefOf={(run) => WORKFRONT_PATHS.run(run.id)}
				/>
				<Pager
					page={page}
					totalPages={query.data?.pagination.totalPages ?? 1}
					total={query.data?.pagination.total ?? 0}
					noun="run"
					nounPlural="runs"
					isFetching={query.isFetching}
					onPage={setPage}
				/>
			</CardShell>
		</div>
	)
}

/** One run with its step runs. */
export function WorkfrontRunDetailPage() {
	const { id = '' } = useParams()
	const query = useWorkfrontRun(id)
	const run = query.data
	if (query.isLoading) return <Spinner aria-label="Loading the run" />
	return (
		<div className="space-y-6">
			<WorkfrontHeader title={run ? `${humanize(run.process)} run` : 'Run'} />
			{!run ? (
				<p className="text-ds-sm text-ds-danger">{apiErrorMessage(query.error, 'Could not load this run.')}</p>
			) : (
				<>
					<div className="flex flex-wrap items-center gap-2 text-ds-sm">
						<RunStatusBadge status={run.status} />
						<span>{humanize(run.trigger)}</span>
						<span className="text-ds-text-muted">
							{formatUtc(run.startedAt)} to {run.finishedAt ? formatUtc(run.finishedAt) : 'now'}
						</span>
					</div>
					<p className="text-ds-sm text-ds-text-muted">{statsLine(run.stats)}</p>
					<SectionCard title="Step runs" count={run.steps.length}>
						<StepRunsTable steps={run.steps} />
					</SectionCard>
				</>
			)}
		</div>
	)
}

/**
 * The readable errors, open first by default, filterable by status, severity and
 * step on the server, and by mapping kind on the page shown (the list API has no
 * mapping-kind filter).
 */
export function WorkfrontErrorsPage() {
	const [status, setStatus] = useState<WorkfrontSyncErrorStatus | undefined>(WorkfrontSyncErrorStatus.OPEN)
	const [severity, setSeverity] = useState<WorkfrontSyncErrorSeverity | undefined>()
	const [step, setStep] = useState<WorkfrontSyncErrorStep | undefined>()
	const [mappingKind, setMappingKind] = useState<WorkfrontMappingKind | undefined>()
	const [page, setPage] = useState(1)
	const query = useWorkfrontErrors({ status, severity, step, page, limit: PAGE_SIZE })
	const errors = (query.data?.data ?? []).filter((error) => !mappingKind || error.mappingKind === mappingKind)
	const { actions, dialog } = useErrorActions()
	useEffect(() => setPage(1), [status, severity, step])
	return (
		<div className="space-y-6">
			<WorkfrontHeader title="Errors" />
			<SectionCard title="Filters">
				<div className="grid gap-4 md:grid-cols-4">
					<EnumFilter
						label="Status"
						values={Object.values(WorkfrontSyncErrorStatus)}
						value={status}
						onChange={setStatus}
					/>
					<EnumFilter
						label="Severity"
						values={Object.values(WorkfrontSyncErrorSeverity)}
						value={severity}
						onChange={setSeverity}
					/>
					<EnumFilter label="Step" values={Object.values(WorkfrontSyncErrorStep)} value={step} onChange={setStep} />
					<EnumFilter
						label="Mapping kind"
						values={Object.values(WorkfrontMappingKind)}
						value={mappingKind}
						onChange={setMappingKind}
					/>
				</div>
			</SectionCard>
			<CardShell>
				<ErrorsTable
					errors={errors}
					isLoading={query.isLoading}
					isError={query.isError}
					hrefOf={(error) => WORKFRONT_PATHS.error(error.id)}
					{...actions}
				/>
				<Pager
					page={page}
					totalPages={query.data?.pagination.totalPages ?? 1}
					total={query.data?.pagination.total ?? 0}
					noun="error"
					nounPlural="errors"
					isFetching={query.isFetching}
					onPage={setPage}
				/>
			</CardShell>
			{dialog}
		</div>
	)
}

/** One error: its explanation, its fix and the affected objects, with Retry and Ignore while it is open. */
export function WorkfrontErrorDetailPage() {
	const { id = '' } = useParams()
	const query = useWorkfrontError(id)
	const { actions, dialog } = useErrorActions()
	if (query.isLoading) return <Spinner aria-label="Loading the error" />
	return (
		<div className="space-y-6">
			<WorkfrontHeader title="Error" />
			{query.data ? (
				<SectionCard title={query.data.code}>
					<ErrorDetailCard error={query.data} {...actions} />
				</SectionCard>
			) : (
				<p className="text-ds-sm text-ds-danger">{apiErrorMessage(query.error, 'Could not load this error.')}</p>
			)}
			{dialog}
		</div>
	)
}
